/**
 * Centralized API Client - SAME-ORIGIN STRATEGY
 * 
 * CRITICAL FIX: For custom domains like app.jyotishpuraan.com,
 * we MUST use the SAME ORIGIN for API calls to avoid CORS issues.
 * 
 * The backend is served from the same domain via ingress/proxy,
 * so we use the current window.location.origin which automatically
 * resolves to the domain the user is accessing.
 */

import axios from 'axios';

/**
 * Get the correct backend URL at RUNTIME - ALWAYS use same origin
 * This ensures API calls go to the same domain the user is on
 */
export const getBackendUrl = () => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  
  const origin = window.location.origin;
  
  // Debug logging - helpful for troubleshooting
  console.log('🚀 [API Client] SAME-ORIGIN Strategy');
  console.log('🚀 [API Client] Using origin:', origin);
  
  // ALWAYS use the current origin - no cross-origin calls
  return origin;
};

/**
 * Make a GET request with runtime URL resolution
 */
export const apiGet = async (endpoint, config = {}) => {
  const baseUrl = getBackendUrl();
  const url = endpoint.startsWith('/') ? `${baseUrl}${endpoint}` : `${baseUrl}/${endpoint}`;
  
  return axios.get(url, {
    ...config,
    withCredentials: false, // Required for CORS with wildcard origins
  });
};

/**
 * Make a POST request with runtime URL resolution
 */
export const apiPost = async (endpoint, data = {}, config = {}) => {
  const baseUrl = getBackendUrl();
  const url = endpoint.startsWith('/') ? `${baseUrl}${endpoint}` : `${baseUrl}/${endpoint}`;
  
  return axios.post(url, data, {
    ...config,
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
      ...config.headers,
    },
  });
};

/**
 * Make a PUT request with runtime URL resolution
 */
export const apiPut = async (endpoint, data = {}, config = {}) => {
  const baseUrl = getBackendUrl();
  const url = endpoint.startsWith('/') ? `${baseUrl}${endpoint}` : `${baseUrl}/${endpoint}`;
  
  return axios.put(url, data, {
    ...config,
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
      ...config.headers,
    },
  });
};

/**
 * Make a DELETE request with runtime URL resolution
 */
export const apiDelete = async (endpoint, config = {}) => {
  const baseUrl = getBackendUrl();
  const url = endpoint.startsWith('/') ? `${baseUrl}${endpoint}` : `${baseUrl}/${endpoint}`;
  
  return axios.delete(url, {
    ...config,
    withCredentials: false,
  });
};

/**
 * Search for places - commonly used across the app
 * This is a convenience function for the location autocomplete feature
 */
export const searchPlaces = async (query) => {
  if (!query || query.length < 2) {
    return { suggestions: [] };
  }
  
  try {
    const response = await apiGet(`/api/search-places?q=${encodeURIComponent(query)}`);
    return response.data;
  } catch (error) {
    console.error('[API Client] Place search error:', error);
    return { suggestions: [] };
  }
};

/**
 * Create an axios instance that resolves URL at request time
 * Use this if you need more control over the axios instance
 */
export const createApiClient = () => {
  const client = axios.create({
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  // Intercept requests to add the correct base URL at runtime
  client.interceptors.request.use((config) => {
    const baseUrl = getBackendUrl();
    
    // If the URL doesn't start with http, prepend the base URL
    if (config.url && !config.url.startsWith('http')) {
      config.url = config.url.startsWith('/') 
        ? `${baseUrl}${config.url}`
        : `${baseUrl}/${config.url}`;
    }
    
    return config;
  });
  
  return client;
};

// Export a pre-configured client instance
export const apiClient = createApiClient();

export default {
  getBackendUrl,
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
  searchPlaces,
  createApiClient,
  apiClient,
};
